import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Modal,
  Slide,
  Box,
  Stack,
  Typography,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  FormControl,
  Skeleton,
} from "@mui/material";
import ReactPerfectScrollbar from "react-perfect-scrollbar";
import { ReactComponent as CloseIcon } from "../../../../vendor/_new/icon/navbar/notification/close_big.svg";
import GroupIcon from "@mui/icons-material/Group";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import { CancelButton, PrimaryButton } from "../../../common/Button";
import useRBAC from "../../../../hooks/user/useRBAC";
import axios from "../../../../utils/axios/v1/userAxios";

const PRIMARY_COLOR = "#6371F7";
const TEXT_PRIMARY = "#1A1A1A";
const TEXT_SECONDARY = "#6B7280";
const BORDER_COLOR = "#E5E7EB";
const ERROR_COLOR = "#DC2626";

const baseFontStyle = {
  fontSize: 14,
  lineHeight: "21px",
};

const selectStyle = {
  ...baseFontStyle,
  height: 36,
  backgroundColor: "#fff",
  borderRadius: "8px",
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: BORDER_COLOR,
    borderRadius: "8px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: PRIMARY_COLOR,
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: PRIMARY_COLOR,
  },
  "& .MuiSelect-select": { 
    ...baseFontStyle,
    py: "7.5px",
  },
};

const menuItemStyle = {
  ...baseFontStyle,
  height: 36,
  "&:hover": { 
    backgroundColor: "#F3F4FF",
    color: PRIMARY_COLOR,
  },
  "&.Mui-selected": {
    backgroundColor: "#EEF0FE",
    color: PRIMARY_COLOR,
    "&:hover": { backgroundColor: "#E8EAFD" },
  },
};

const tableCellStyle = {
  ...baseFontStyle,
  py: 1.5,
  borderBottom: `1px solid ${BORDER_COLOR}`,
};

const tableHeaderStyle = {
  ...baseFontStyle,
  fontWeight: 500,
  color: TEXT_SECONDARY,
  py: 1.5,
  backgroundColor: "#fff",
  borderBottom: `1px solid ${BORDER_COLOR}`,
};

const isActiveUser = (user) => {
  if (!user) return false;
  return !user.deleted && !user.isDeleted;
};

const ProjectMembersDrawer = ({ open, onClose, project }) => {
  const { getRoles, roles, getProjectMembers, addProjectMember, updateProjectMemberRole, removeProjectMember } = useRBAC();
  
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [orgUsers, setOrgUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedRoleId, setSelectedRoleId] = useState("");
  const [editingMemberId, setEditingMemberId] = useState(null);
  const [editingRoleId, setEditingRoleId] = useState("");
  const [addMemberTouched, setAddMemberTouched] = useState({});

  const addMemberValid = Boolean(selectedUserId && selectedRoleId);
  const showAddMemberErr = (field) => addMemberTouched[field] && !(
    field === "user" ? selectedUserId : selectedRoleId
  );

  const fetchMembers = useCallback(async () => {
    if (!project?.id) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getProjectMembers(project.id);
      setMembers(response?.members || response || []);
    } catch (err) {
      setError(err.message || "Failed to fetch project members");
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, [project?.id, getProjectMembers]);

  const fetchOrgUsers = useCallback(async () => {
    try {
      const response = await axios.get("/users");
      setOrgUsers(response.data || []);
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setOrgUsers([]);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      setSelectedUserId("");
      setSelectedRoleId("");
      setEditingMemberId(null);
      setEditingRoleId("");
      setAddMemberTouched({});
      setError(null);
    }
  }, [open]);

  useEffect(() => {
    if (open && project?.id) {
      fetchMembers();
      fetchOrgUsers();
      getRoles().catch(() => {});
    }
  }, [open, project?.id, fetchMembers, fetchOrgUsers, getRoles]);

  const activeMembers = useMemo(
    () => members.filter((member) => isActiveUser(member.user) && !member.deleted),
    [members]
  );

  const availableUsers = useMemo(() => {
    const memberUserIds = new Set(activeMembers.map((m) => m.user_id || m.user?.id || m.userId));
    return orgUsers.filter(
      (user) => isActiveUser(user) && !memberUserIds.has(user.id || user._id)
    );
  }, [orgUsers, activeMembers]);

  const roleOptions = useMemo(() => {
    return (roles || []).map((role) => ({
      value: role.id || role._id,
      label: role.name,
      isCustom: role.type === "custom",
    }));
  }, [roles]);

  const handleAddMember = async () => {
    setAddMemberTouched({ user: true, role: true });
    if (!project?.id || !selectedUserId || !selectedRoleId) return;
    setSaving(true);
    setError(null);
    try {
      await addProjectMember(project.id, selectedUserId, selectedRoleId);
      await fetchMembers();
      setSelectedUserId("");
      setSelectedRoleId("");
      setAddMemberTouched({});
    } catch (err) {
      setError(err.message || "Failed to add member");
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateRole = async (membershipId) => {
    if (!project?.id || !editingRoleId) return;
    setSaving(true);
    setError(null);
    try {
      await updateProjectMemberRole(project.id, membershipId, editingRoleId);
      await fetchMembers();
      setEditingMemberId(null);
      setEditingRoleId("");
    } catch (err) {
      setError(err.message || "Failed to update member role");
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (membershipId) => {
    if (!project?.id) return;
    setSaving(true);
    setError(null);
    try {
      await removeProjectMember(project.id, membershipId);
      await fetchMembers();
    } catch (err) {
      setError(err.message || "Failed to remove member");
    } finally {
      setSaving(false);
    }
  };

  const startEditing = (member) => {
    setEditingMemberId(member.id || member._id);
    setEditingRoleId(member.role_id || member.role?.id || member.roleId || "");
  };

  const cancelEditing = () => {
    setEditingMemberId(null);
    setEditingRoleId("");
  };

  const getMemberName = (member) => {
    if (member.user?.firstName || member.user?.lastName) {
      return `${member.user.firstName || ""} ${member.user.lastName || ""}`.trim();
    }
    if (member.firstName || member.lastName) {
      return `${member.firstName || ""} ${member.lastName || ""}`.trim();
    }
    return member.user?.email || member.email || "Unknown";
  };

  const getMemberEmail = (member) => {
    return member.user?.email || member.email || "";
  };

  const getMemberRoleName = (member) => {
    return member.role?.name || member.roleName || "Unknown";
  };

  const handleClose = () => {
    onClose();
  };

  if (!open) return null;

  return (
    <Modal 
      open={open} 
      onClose={handleClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: 'rgba(0, 0, 0, 0.5)' }
        }
      }}
    >
      <Slide direction="left" in={open} mountOnEnter unmountOnExit>
        <Stack
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            width: "40%",
            minWidth: 560,
            height: "100vh",
            backgroundColor: "#fff",
            boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
            zIndex: 1300,
          }}
        >
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="center"
            sx={{ 
              px: 3, 
              py: 2.5,
              borderBottom: `1px solid ${BORDER_COLOR}`,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  backgroundColor: "#EEF0FE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <GroupIcon sx={{ width: 20, height: 20, color: PRIMARY_COLOR }} />
              </Box>
              <Stack spacing={0.25}>
                <Typography
                  sx={{ 
                    fontWeight: 500, 
                    fontSize: 18, 
                    lineHeight: "24px", 
                    color: TEXT_PRIMARY 
                  }}
                >
                  Project Members
                </Typography>
                <Typography 
                  sx={{ 
                    fontSize: 14, 
                    lineHeight: "20px", 
                    color: TEXT_SECONDARY 
                  }}
                >
                  {project?.name || "Project"}
                </Typography>
              </Stack>
            </Stack>
            <IconButton 
              onClick={handleClose} 
              sx={{ 
                width: 36, 
                height: 36, 
                borderRadius: "8px",
                "&:hover": { backgroundColor: "#F3F4F6" },
              }}
            >
              <CloseIcon style={{ width: 20, height: 20, color: TEXT_SECONDARY }} />
            </IconButton>
          </Stack>

          <Box sx={{ flex: 1, overflow: "hidden" }}>
            <ReactPerfectScrollbar>
              <Stack spacing={3} sx={{ p: 3 }}>
                {error && (
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: "8px",
                      backgroundColor: "#FEF2F2",
                      border: `1px solid ${ERROR_COLOR}`,
                    }}
                  >
                    <Typography sx={{ ...baseFontStyle, color: ERROR_COLOR }}>
                      {error}
                    </Typography>
                  </Box>
                )}

                <Stack
                  spacing={2}
                  sx={{
                    p: 2.5,
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    border: `1px solid ${BORDER_COLOR}`,
                  }}
                >
                  <Typography sx={{ ...baseFontStyle, fontWeight: 500, color: TEXT_PRIMARY }}>
                    Add New Member
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <FormControl size="small" sx={{ flex: 1 }} error={showAddMemberErr("user")}>
                      <Select
                        value={selectedUserId}
                        onChange={(e) => setSelectedUserId(e.target.value)}
                        onBlur={() => setAddMemberTouched((prev) => ({ ...prev, user: true }))}
                        displayEmpty
                        disabled={saving || loading}
                        sx={selectStyle}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              mt: 0.5,
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled sx={menuItemStyle}>
                          Select User
                        </MenuItem>
                        {availableUsers.map((user) => (
                          <MenuItem key={user.id || user._id} value={user.id || user._id} sx={menuItemStyle}>
                            {user.firstName || user.lastName
                              ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
                              : user.email}
                          </MenuItem>
                        ))}
                      </Select>
                      {showAddMemberErr("user") && (
                        <Typography sx={{ fontSize: 12, color: ERROR_COLOR, mt: 0.5 }}>
                          Select a user
                        </Typography>
                      )}
                    </FormControl>

                    <FormControl size="small" sx={{ flex: 1 }} error={showAddMemberErr("role")}>
                      <Select
                        value={selectedRoleId}
                        onChange={(e) => setSelectedRoleId(e.target.value)}
                        onBlur={() => setAddMemberTouched((prev) => ({ ...prev, role: true }))}
                        displayEmpty
                        disabled={saving || loading}
                        sx={selectStyle}
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                              mt: 0.5,
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled sx={menuItemStyle}>
                          Select Role
                        </MenuItem>
                        {roleOptions.map((role) => (
                          <MenuItem key={role.value} value={role.value} sx={menuItemStyle}>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <span>{role.label}</span>
                              {role.isCustom && (
                                <Box
                                  component="span"
                                  sx={{
                                    px: 1,
                                    py: 0.25,
                                    borderRadius: "4px",
                                    backgroundColor: "#EEF0FE",
                                    color: PRIMARY_COLOR,
                                    fontSize: 12,
                                    fontWeight: 500,
                                  }}
                                >
                                  Custom
                                </Box>
                              )}
                            </Stack>
                          </MenuItem>
                        ))}
                      </Select>
                      {showAddMemberErr("role") && (
                        <Typography sx={{ fontSize: 12, color: ERROR_COLOR, mt: 0.5 }}>
                          Select a role
                        </Typography>
                      )}
                    </FormControl>
                  </Stack>
                  <Stack direction="row" justifyContent="flex-end">
                    <PrimaryButton
                      onClick={handleAddMember}
                      disabled={!addMemberValid || saving || !project?.id || loading}
                    >
                      {saving ? "Adding..." : "Add Member"}
                    </PrimaryButton>
                  </Stack>
                </Stack>

                <Box 
                  sx={{ 
                    borderRadius: "8px", 
                    border: `1px solid ${BORDER_COLOR}`, 
                    overflow: "hidden",
                    backgroundColor: "#fff",
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={tableHeaderStyle}>Name</TableCell>
                        <TableCell sx={tableHeaderStyle}>Email</TableCell>
                        <TableCell sx={tableHeaderStyle}>Role</TableCell>
                        <TableCell sx={{ ...tableHeaderStyle, width: 100 }}>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        [1, 2, 3].map((i) => (
                          <TableRow key={i}>
                            <TableCell sx={tableCellStyle}><Skeleton variant="text" sx={{ }} /></TableCell>
                            <TableCell sx={tableCellStyle}><Skeleton variant="text" sx={{ }} /></TableCell>
                            <TableCell sx={tableCellStyle}><Skeleton variant="text" sx={{ }} /></TableCell>
                            <TableCell sx={tableCellStyle}><Skeleton variant="text" width={60} sx={{ }} /></TableCell>
                          </TableRow>
                        ))
                      ) : activeMembers.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ ...tableCellStyle, textAlign: "center", py: 4, borderBottom: "none" }}>
                            <Typography sx={{ ...baseFontStyle, color: TEXT_SECONDARY }}>
                              No members in this project
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        activeMembers.map((member, index) => {
                          const memberId = member.id || member._id;
                          const isEditing = editingMemberId === memberId;
                          const isLast = index === activeMembers.length - 1;

                          return (
                            <TableRow key={memberId} sx={{ "&:hover": { backgroundColor: "#FAFAFA" } }}>
                              <TableCell sx={{ ...tableCellStyle, color: TEXT_PRIMARY, borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                {getMemberName(member)}
                              </TableCell>
                              <TableCell sx={{ ...tableCellStyle, color: TEXT_SECONDARY, borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                {getMemberEmail(member)}
                              </TableCell>
                              <TableCell sx={{ ...tableCellStyle, borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                {isEditing ? (
                                  <Stack direction="row" alignItems="center" spacing={1}>
                                    <FormControl size="small" sx={{ minWidth: 120 }}>
                                      <Select
                                        value={editingRoleId}
                                        onChange={(e) => setEditingRoleId(e.target.value)}
                                        sx={{ ...selectStyle, height: 32 }}
                                        MenuProps={{
                                          PaperProps: {
                                            sx: {
                                              borderRadius: "8px",
                                              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                                              mt: 0.5,
                                            },
                                          },
                                        }}
                                      >
                                        {roleOptions.map((role) => (
                                          <MenuItem key={role.value} value={role.value} sx={menuItemStyle}>
                                            {role.label}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                    <Button
                                      size="small"
                                      onClick={() => handleUpdateRole(memberId)}
                                      disabled={saving}
                                      sx={{ 
                                        ...baseFontStyle, 
                                        height: 32,
                                        minWidth: "auto",
                                        px: 1.5,
                                        textTransform: "none", 
                                        color: PRIMARY_COLOR,
                                        fontWeight: 500,
                                        "&:hover": { backgroundColor: "#F3F4FF" },
                                      }}
                                    >
                                      Save
                                    </Button>
                                    <Button
                                      size="small"
                                      onClick={cancelEditing}
                                      sx={{ 
                                        ...baseFontStyle, 
                                        height: 32,
                                        minWidth: "auto",
                                        px: 1.5,
                                        textTransform: "none", 
                                        color: TEXT_SECONDARY,
                                        "&:hover": { backgroundColor: "#F3F4F6" },
                                      }}
                                    >
                                      Cancel
                                    </Button>
                                  </Stack>
                                ) : (
                                  <Box
                                    sx={{
                                      display: "inline-flex",
                                      alignItems: "center",
                                      px: 1.5,
                                      py: 0.5,
                                      borderRadius: "8px",
                                      backgroundColor: "#F3F4F6",
                                      ...baseFontStyle,
                                      color: TEXT_PRIMARY,
                                    }}
                                  >
                                    {getMemberRoleName(member)}
                                  </Box>
                                )}
                              </TableCell>
                              <TableCell sx={{ ...tableCellStyle, borderBottom: isLast ? "none" : `1px solid ${BORDER_COLOR}` }}>
                                {!isEditing && (
                                  <Stack direction="row" spacing={0.5}>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => startEditing(member)} 
                                      disabled={saving}
                                      sx={{ 
                                        width: 32, 
                                        height: 32, 
                                        borderRadius: "8px",
                                        "&:hover": { backgroundColor: "#F3F4FF" },
                                      }}
                                    >
                                      <EditIcon sx={{ fontSize: 18, color: PRIMARY_COLOR }} />
                                    </IconButton>
                                    <IconButton 
                                      size="small" 
                                      onClick={() => handleRemoveMember(memberId)} 
                                      disabled={saving}
                                      sx={{ 
                                        width: 32, 
                                        height: 32, 
                                        borderRadius: "8px",
                                        "&:hover": { backgroundColor: "#FEF2F2" },
                                      }}
                                    >
                                      <DeleteOutlineIcon sx={{ fontSize: 18, color: ERROR_COLOR }} />
                                    </IconButton>
                                  </Stack>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Stack>
            </ReactPerfectScrollbar>
          </Box>

          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{
              px: 3,
              py: 2,
              borderTop: `1px solid ${BORDER_COLOR}`,
            }}
          >
            <CancelButton
              onClick={handleClose}
              disabled={saving}
              label="Close"
              sx={{ width: "auto", px: 3 }}
            />
          </Stack>
        </Stack>
      </Slide>
    </Modal>
  );
};

export default ProjectMembersDrawer;
